import React from 'react';
import { FileText, Users, DollarSign, AlertTriangle, Shield, Phone, XCircle, Clock, Lock } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { useI18n } from '@/i18n';

const TermsOfServiceCustomersPage: React.FC = () => {
  const { locale } = useI18n();

  if (locale === 'zh-CN') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-primary py-16 text-white">
          <div className="container mx-auto px-4">
            <div className="mb-4 flex items-center justify-center">
              <FileText className="mr-4 h-12 w-12" />
              <h1 className="text-4xl font-bold md:text-5xl">服务条款</h1>
            </div>
            <p className="mb-2 text-center text-2xl font-semibold text-accent">适用于家长与学生</p>
            <p className="mx-auto max-w-3xl text-center text-gray-200">生效日期：2026 年 4 月 23 日</p>
            <p className="mx-auto mt-2 max-w-3xl text-center text-gray-200">最后更新：2026 年 4 月 23 日</p>
          </div>
        </div>

        <div className="container mx-auto max-w-5xl px-4 py-12">
          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Users className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">1. 平台概览</h2>
                <div className="space-y-4">
                  <p>
                    Tutor Founder 是马来西亚的补习配对平台，连接家长、学生与合适导师，并在前期付款阶段提供托管式付款安排。
                  </p>
                  <p>
                    平台并非雇主、代理或教育机构本身，不直接提供补习服务，也不对学习成果或双方私人纠纷承担保证责任。
                  </p>
                  <p className="font-semibold text-primary">使用平台即表示你同意本条款、隐私政策以及适用的马来西亚法律与规定。</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <DollarSign className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">2. 账单与付款周期</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>成功配对后，你需要先向 Tutor Founder 支付每月固定学费</li>
                  <li>第 1-2 个月平台会从付款中扣除 25% 行政佣金，其余 75% 拨付导师</li>
                  <li>第 2 个月账单会通过官方 WhatsApp 渠道发出；若未按时付款，服务可能暂停</li>
                  <li>第 3 个月起，家长/学生将直接与导师结算，平台佣金为 0%</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Clock className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">3. 课程验证与导师拨款</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>课程安排、出席确认与行政沟通必须通过 Tutor Founder 官方 WhatsApp 渠道进行</li>
                  <li>每次课程后，系统会通过 WhatsApp 向你确认出席情况</li>
                  <li>若 24 小时内未回复，课程会自动标记为“已完成”，以避免导师拨款延误</li>
                  <li>课程确认后，平台会按月度拨款周期向导师发放款项</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <XCircle className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">4. 取消、补课与托管款项</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>学生因病或个人原因缺课时，该课时通常不会直接退款，而会转为补课额度</li>
                  <li>补课通常需在原课程日起 30 天内完成，逾期可能失效</li>
                  <li>错过课程的相关款项会由平台暂时托管，直至补课真正完成并确认</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Lock className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">5. 平台完整性与反绕过规则</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>在初始介绍后的 12 个月保护期内，你不得绕过平台与导师私下付款以规避平台费用</li>
                  <li>若尝试规避平台收费规则，平台可暂停或永久终止账户</li>
                  <li>如导师施压要求绕过平台付款，家长/学生应尽快向客服举报</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <DollarSign className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">6. 退款政策</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>退款一般会在 5 至 14 个工作天内处理，最终以平台审核结果为准</li>
                  <li>导师无故缺席、临时取消、严重失实陈述、不当行为，或第一堂课后 24 小时内提出的不满意情况，可能符合退款条件</li>
                  <li>家长/学生主动取消、病假（改为补课额度）、学习进展不如预期，或逾时投诉，通常不属于可退款范围</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <AlertTriangle className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">7. 家长/学生行为责任</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>不得提供虚假资料、骚扰导师或平台员工、进行欺诈付款、分享密码、或干扰平台运作</li>
                  <li>应以尊重且专业的方式对待导师，并为面授课程提供安全、合适的学习环境</li>
                  <li>应如实说明学生程度、学习需求及特殊要求，并及时回复平台的重要通知</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <AlertTriangle className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">8. 平台责任限制</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>Tutor Founder 仅为中介平台，不直接提供补习服务，也不保证导师表现、学习成果或持续可用性</li>
                  <li>平台不对导师行为、双方现实接触、私人纠纷或安全事故承担直接责任</li>
                  <li>在法律允许范围内，平台的总责任将受到适用条款限制</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Phone className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">9. 争议处理</h2>
                <ul className="ml-6 list-disc space-y-2">
                  <li>若与导师发生争议，应先尝试直接沟通解决</li>
                  <li>如无法自行解决，可联络 Tutor Founder 寻求调解协助</li>
                  <li>正式投诉应在事件发生后 14 天内以书面形式提交</li>
                  <li>本条款受马来西亚法律管辖，并由马来西亚法院专属管辖</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Shield className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">10. 隐私与资料保护</h2>
                <p>
                  你使用 Tutor Founder 的同时，也受我们的 <Link to="/privacy-policy" className="font-semibold text-accent hover:underline">隐私政策</Link> 约束。该政策说明我们如何按照马来西亚 PDPA 收集、使用与保护你的个人资料。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <FileText className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">11. 条款更新</h2>
                <p>
                  Tutor Founder 可随时更新本条款。若有重大修改，我们会在平台上发出明显通知。你在修改后继续使用平台，即表示接受更新后的条款。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8 rounded-lg bg-white p-8 shadow-md">
            <div className="mb-4 flex items-start">
              <Phone className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary" />
              <div className="w-full text-gray-700">
                <h2 className="mb-4 text-2xl font-bold text-primary">12. 联络资料</h2>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-6">
                  <p className="mb-3 font-semibold text-primary">Tutor Founder 客服支援</p>
                  <div className="space-y-2">
                    <p><strong>地址：</strong> Kuala Lumpur, Malaysia</p>
                    <p><strong>电邮：</strong> tutorfounder@gmail.com</p>
                    <p><strong>电话：</strong> +60 11-12049396</p>
                    <p><strong>办公时间：</strong>星期一至星期五，9:00 AM - 6:00 PM (GMT+8)</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-lg bg-primary p-8 text-white shadow-md">
            <h2 className="mb-4 text-2xl font-bold">13. 确认与同意</h2>
            <div className="space-y-4">
              <p>作为家长或学生使用 Tutor Founder，即表示你已阅读、理解并同意受本服务条款约束。</p>
              <p className="font-semibold">若你不同意本条款的任何部分，请立即停止使用本平台。</p>
            </div>
          </section>

          <div className="mt-12 border-t border-gray-200 pt-8 text-center text-gray-600">
            <p className="text-sm">感谢你选择 Tutor Founder。我们会持续为马来西亚家庭提供更好的导师配对体验。</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary text-white py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-4">
            <FileText className="h-12 w-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-center text-2xl font-semibold text-accent mb-2">
            For Parents & Students
          </p>
          <p className="text-center text-gray-200 max-w-3xl mx-auto">
            Effective Date: April 23, 2026
          </p>
          <p className="text-center text-gray-200 max-w-3xl mx-auto mt-2">
            Last Updated: April 23, 2026
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 max-w-5xl">

        {/* Platform Overview */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Users className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold text-primary mb-4">1. Platform Overview</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  <strong>Tutor Founder</strong> operates as a dedicated tuition matching platform for academic and music education in Malaysia. We connect students with qualified tutors and facilitate secure initial payments via an escrow model.
                </p>
                <p>
                  <strong>Important Disclaimer:</strong> Tutor Founder is not an employer, agent, or educational provider, and bears no liability for academic outcomes or interpersonal disputes.
                </p>
                <p>
                  By registering, accessing, or using Tutor Founder, you acknowledge that you have read, understood, and agree to be bound by these Terms, our Privacy Policy, and all applicable laws and regulations of Malaysia.
                </p>
                <p className="font-semibold text-primary">
                  If you do not agree to these Terms, you must not use our services.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Billing and Payment Cycle */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <DollarSign className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">2. Billing & Payment Cycle</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">2.1 Platform Billing (Months 1 & 2)</h3>
                  <p className="mb-2">
                    To secure a match, you must pay a fixed monthly tuition upfront to Tutor Founder. The platform deducts a <strong>25% administrative commission</strong> from this payment before disbursing the remaining <strong>75% to the tutor</strong>.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">2.2 Month 2 Invoicing</h3>
                  <p className="mb-2">
                    The invoice for Month 2 will be issued via official WhatsApp channels at the end of the second month. <strong>Failure to remit payment will result in a pause of services.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">2.3 Direct Billing (Month 3 Onward)</h3>
                  <p className="mb-2">
                    Upon successful completion of the two-month introductory period, the platform billing cycle concludes. From Month 3 forward, <strong>you pay the tutor directly according to mutually agreed terms. Tutor Founder claims a 0% commission from Month 3 onward.</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lesson Verification and Payment */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Clock className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">3. Lesson Verification & Tutor Payouts</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.1 Official Communication</h3>
                  <p className="mb-2">
                    All scheduling, attendance verification, and administrative coordination must be conducted via <strong>Tutor Founder's official WhatsApp channels.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.2 Verification</h3>
                  <p className="mb-2">
                    Following a scheduled session, attendance will be <strong>verified with you via WhatsApp.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.3 24-Hour Auto-Complete</h3>
                  <p className="mb-2">
                    If you do not reply to the WhatsApp verification prompt within <strong>24 hours</strong>, the system automatically defaults the lesson status to <strong>"Completed"</strong> to prevent payment delays for the tutor.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">3.4 End of Month Payouts</h3>
                  <p className="mb-2">
                    Upon verification or auto-completion, Tutor Founder will process the tutor's 75% payout on a monthly disbursement schedule at the end of the month.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cancellations, Makeup Classes & Escrow */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <XCircle className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">4. Cancellations, Makeup Classes & Escrow</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.1 No Refunds for Sick Days</h3>
                  <p className="mb-2">
                    If a student misses a scheduled lesson due to illness or personal emergencies, the funds for that lesson are <strong>non-refundable</strong>. The missed lesson converts into a <strong>"Makeup Credit."</strong> The standard monthly billing cycle will not be paused or delayed.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.2 Makeup Scheduling & Expiry</h3>
                  <p className="mb-2">
                    The tutor and client must coordinate via official WhatsApp channels to schedule the makeup class. <strong>Makeup Credits must be fulfilled within 30 days</strong> of the originally missed class, after which the credit expires.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">4.3 Escrow Security</h3>
                  <p className="mb-2">
                    Payouts for missed classes are strictly held in escrow by the platform. Funds are not released to the tutor until the rescheduled makeup class is officially taught and verified.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Integrity & Non-Circumvention */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Lock className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">5. Platform Integrity & Non-Circumvention (12-Month Rule)</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  To maintain a sustainable marketplace, users must honor the introductory billing phase.
                </p>
                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.1 12-Month Non-Circumvention</h3>
                  <p className="mb-2">
                    Users are strictly prohibited from bypassing the platform to coordinate direct payments during Month 1 and Month 2. <strong>This protection period remains active for 12 months from the date of the initial introduction.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.2 Breach of Terms</h3>
                  <p className="mb-2">
                    Attempting to circumvent the platform to avoid the initial commissions will result in <strong>immediate, permanent account suspension.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">5.3 Safe Harbor Reporting</h3>
                  <p className="mb-2">
                    Clients who are pressured by tutors to bypass the platform's payment systems are encouraged to report this behavior to Customer Service. <strong>Clients who report such violations are protected from account suspension and remain in good standing.</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Refund Policy */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <DollarSign className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">6. Refund Policy</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.1 Refund Processing Timeline</h3>
                  <p className="mb-2">
                    Refunds are processed at the platform's discretion within <strong>5–14 working days.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.2 Eligible for Refund</h3>
                  <p className="mb-2">The following scenarios qualify for refunds:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Tutor no-shows (without 24 hours' notice)</li>
                    <li>Last-minute cancellations by the tutor (within 24 hours of scheduled lesson)</li>
                    <li>Verified misrepresentation</li>
                    <li>Serious misconduct</li>
                    <li>Dissatisfaction with the first session (must be reported within 24 hours of completion)</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">6.3 Non-Refundable Scenarios</h3>
                  <p className="mb-2">The following are <strong>NOT refundable:</strong></p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Client cancellations</li>
                    <li>Sick days (subject to makeup credits)</li>
                    <li>Lack of academic improvement</li>
                    <li>Complaints filed outside the specified reporting windows</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Conduct and Responsibilities */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <AlertTriangle className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">7. Customer Conduct and Responsibilities</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.1 Prohibited Activities</h3>
                  <p className="mb-2">You must <strong>NOT</strong>:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Use any device, software, or program to interfere with the normal operation of the Tutor Founder platform</li>
                    <li>Engage in any activity that imposes an unreasonable or disproportionately large load on our infrastructure</li>
                    <li>Share your account password with any third party</li>
                    <li>Provide false, misleading, or inaccurate information</li>
                    <li>Harass, threaten, or abuse tutors or Tutor Founder staff</li>
                    <li>Engage in fraudulent payment activities</li>
                    <li>Attempt to bypass Tutor Founder's payment system by paying tutors directly to avoid platform fees</li>
                    <li>Post or transmit any unlawful, defamatory, or inappropriate content</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.2 Respectful Treatment of Tutors</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>You must treat tutors with respect and professionalism</li>
                    <li>Unreasonable requests or abusive behavior may result in account suspension or termination</li>
                    <li>You are responsible for ensuring a safe and conducive learning environment for lessons conducted at your premises</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.3 Accurate Lesson Information</h3>
                  <p>
                    You must provide accurate information about your student's learning needs, level, and any special requirements when booking a tutor.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">7.4 Communication with Tutor Founder</h3>
                  <p>
                    You must respond promptly to communications from Tutor Founder, especially regarding payment confirmations, lesson confirmations, and dispute resolution.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Liability and Disclaimers */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <AlertTriangle className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">8. Platform Liability and Disclaimers</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.1 Intermediary Role</h3>
                  <p>
                    Tutor Founder acts solely as an intermediary platform to facilitate connections between customers and tutors. <strong>We do not provide tutoring services directly.</strong>
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.2 No Liability for Tutor Conduct</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li><strong>Tutor Founder is not liable</strong> for the quality, effectiveness, or outcome of tutoring services provided by tutors</li>
                    <li>We are not responsible for any conflicts, disputes, or incidents that arise from physical meetings or interactions between you (or your student) and tutors</li>
                    <li>We do not guarantee tutor availability, punctuality, or performance</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.3 Tutor Verification Limitations</h3>
                  <p>
                    While we verify tutor identity information (IC numbers, passport details, and educational qualifications), we conduct comprehensive verification checks. <strong>You are encouraged to exercise due diligence when selecting tutors</strong> and to request additional verification if needed.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.4 No Liability for Unreasonable Requests or Disputes</h3>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Tutor Founder is not liable for unreasonable requests made by customers or tutors</li>
                    <li>Any disputes between you and tutors must be resolved directly between the parties</li>
                    <li>Tutor Founder may provide mediation assistance upon request but is under no obligation to do so</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.5 Limitation of Liability</h3>
                  <p className="mb-2">
                    To the fullest extent permitted by Malaysian law, Tutor Founder shall not be liable for:
                  </p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                    <li>Loss of profits, revenue, data, or goodwill</li>
                    <li>Damages arising from your use or inability to use our platform</li>
                    <li>Damages resulting from tutor conduct, lesson outcomes, or safety incidents</li>
                  </ul>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">8.6 Maximum Liability Cap</h3>
                  <p>
                    In any event, Tutor Founder's total liability to you shall not exceed the total amount of fees you paid to Tutor Founder in the 12 months preceding the claim.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Dispute Resolution */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Phone className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">9. Dispute Resolution</h2>
              <div className="text-gray-700 space-y-4">

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.1 Internal Resolution Process</h3>
                  <p>
                    If you have a dispute with a tutor (regarding lesson quality, conduct, payment, etc.), you should first attempt to resolve it directly with the tutor.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.2 Mediation Assistance</h3>
                  <p>
                    If you are unable to resolve the dispute directly, you may contact Tutor Founder for mediation assistance. We will make reasonable efforts to facilitate a resolution.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.3 Formal Complaints</h3>
                  <p>
                    Formal complaints must be submitted in writing to Tutor Founder within <strong>14 days</strong> of the incident. We will investigate and respond within a reasonable timeframe.
                  </p>
                </div>

                <div className="ml-4">
                  <h3 className="text-xl font-semibold text-secondary mb-3">9.4 Governing Law and Jurisdiction</h3>
                  <p>
                    These Terms shall be governed by and construed in accordance with the laws of Malaysia. Any legal disputes shall be subject to the exclusive jurisdiction of the courts of Malaysia.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy and Data Protection */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Shield className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">10. Privacy and Data Protection</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  Your use of Tutor Founder is also governed by our <Link to="/privacy-policy" className="text-accent hover:underline font-semibold">Privacy Policy</Link>, which explains how we collect, use, and protect your personal information in compliance with the Personal Data Protection Act 2010 (PDPA) of Malaysia.
                </p>
                <p>
                  Key points:
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>We collect personal information (contact details, payment information, student details) to provide our services</li>
                  <li>We share limited information with tutors to facilitate lesson arrangements</li>
                  <li>We use secure payment processing and data encryption</li>
                  <li>You have rights to access, correct, and request deletion of your personal data</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Modifications to Terms */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <FileText className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">11. Modifications to Terms</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  Tutor Founder reserves the right to modify these Terms at any time. If we make material changes, we will post a prominent notice on our platform.
                </p>
                <p>
                  Your continued use of Tutor Founder after such modifications constitutes your acceptance of the updated Terms.
                </p>
                <p className="font-semibold text-primary">
                  We encourage you to review these Terms periodically.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-start mb-4">
            <Phone className="h-6 w-6 text-primary mr-3 mt-1 flex-shrink-0" />
            <div className="w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">12. Contact Information</h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  If you have any questions, concerns, or complaints regarding these Terms, please contact us:
                </p>

                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <p className="font-semibold text-primary mb-3">Tutor Founder Customer Support</p>
                  <div className="space-y-2">
                    <p><strong>Address:</strong> Kuala Lumpur, Malaysia</p>
                    <p><strong>Email:</strong> <a href="mailto:tutorfounder@gmail.com" className="text-accent hover:underline">tutorfounder@gmail.com</a></p>
                    <p><strong>Phone:</strong> <a href="tel:+601112049396" className="text-accent hover:underline">+60 11-12049396</a></p>
                    <p><strong>Operating Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM (GMT+8)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Acknowledgment */}
        <section className="bg-primary text-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold mb-4">13. Acknowledgment and Consent</h2>
          <div className="space-y-4">
            <p>
              By using Tutor Founder as a customer (parent or student), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
            <p className="font-semibold">
              If you do not agree with any part of these Terms, you must discontinue use of our platform immediately.
            </p>
          </div>
        </section>

        {/* Footer Note */}
        <div className="text-center text-gray-600 mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm">
            Thank you for choosing Tutor Founder. We are committed to providing you with the best tutor-matching experience in Malaysia.
          </p>
        </div>

      </div>
    </div>
  );
};

export default TermsOfServiceCustomersPage;
